import * as Accordion from '@radix-ui/react-accordion';
import cn from 'classnames';
import { FC, ReactNode, useRef, useState } from 'react';

import { useMedia } from '@/shared/hooks';
import { Icon } from '@/shared/ui';

import { ensureRefreshImageSrc } from '@/entities/assets';

import { StudyAccordionOption } from '../../config';

import styles from './StudyAccordionSelect.module.scss';

type StudyAccordionSelectProps = {
  options: StudyAccordionOption[];
  children?: ReactNode;
  className?: string;
  name?: string;
  placeholder?: ReactNode;
  testID?: string;
  value?: string;
  onChange: (id: string) => void;
  // Extra control next to the trigger, e.g. a reset icon that hides while open
  renderTrailing?: (isOpen: boolean) => ReactNode;
};

const ACCORDION_VALUE = 'study-accordion';

export const StudyAccordionSelect: FC<StudyAccordionSelectProps> = (props) => {
  const {
    options,
    children,
    className,
    name,
    placeholder,
    testID,
    value,
    onChange,
    renderTrailing,
  } = props;

  const { isMobile } = useMedia();
  // NOTE: Need to avoid fade in animation on open OrderReportModal
  const [dirty, setDirty] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const selected = options.find((option) => option.id === value);

  const renderPreview = (
    option: StudyAccordionOption,
    wrapperClassName: string,
    imageClassName: string,
    fallbackClassName: string,
  ) => (
    <div
      className={cn(
        wrapperClassName,
        option.isPlaceholder && styles.placeholder,
      )}
    >
      <img
        src={option.previewSrc}
        alt=""
        className={cn(
          option.isPlaceholder ? fallbackClassName : imageClassName,
          option.contain && styles.contain,
          option.naturalHeight && styles.naturalHeight,
        )}
        onError={ensureRefreshImageSrc}
      />
    </div>
  );

  return (
    <Accordion.Root
      className={cn(styles.root, className)}
      data-component="study-accordion-select"
      data-testid={testID}
      type="single"
      onValueChange={(nextValue) => {
        setIsOpen(nextValue === ACCORDION_VALUE);
        setDirty(true);
      }}
      collapsible
    >
      <Accordion.Item className={styles.item} value={ACCORDION_VALUE}>
        <Accordion.Trigger ref={triggerRef}>
          <div className={styles.trigger}>
            <div className={cn(styles.triggerStudy, dirty && styles.dirty)}>
              {selected ? (
                <>
                  {renderPreview(
                    selected,
                    styles.triggerImgWrapper,
                    styles.triggerImg,
                    styles.triggerImgFallback,
                  )}

                  <p className="p2">{selected.triggerLabel}</p>
                </>
              ) : (
                <div className={cn(styles.triggerPlaceholder, 'p2')}>
                  {placeholder}
                </div>
              )}
            </div>

            <div className={styles.chevron}>
              <Icon name="arrowDown" size={24} />
            </div>
          </div>
        </Accordion.Trigger>

        <Accordion.Content className={styles.content}>
          <div className={styles.studies}>
            {options.map((option) => (
              <label key={option.id} className={styles.studyLabel}>
                <input
                  type="radio"
                  name={name}
                  value={option.id}
                  checked={value === option.id}
                  onClick={() => {
                    if (
                      triggerRef.current !== null &&
                      triggerRef.current.dataset.state === 'open' // WARN: this hack needs to prevent reopen accordion on rappidly clicking on study
                    ) {
                      triggerRef.current.click();
                    }
                  }}
                  onChange={() => onChange(option.id)}
                />

                <div className={styles.study}>
                  {renderPreview(
                    option,
                    styles.studyImgWrapper,
                    styles.studyImg,
                    styles.studyImgFallback,
                  )}

                  <div className={styles.studyInfo}>
                    <h6 className={cn(isMobile ? 'h3m' : 'h6')}>
                      {option.title}
                    </h6>

                    {option.description && (
                      <p className={cn(isMobile ? 'p3m' : 'p2')}>
                        {option.description}
                      </p>
                    )}
                  </div>
                </div>
              </label>
            ))}
          </div>

          {children}
        </Accordion.Content>
      </Accordion.Item>

      {renderTrailing?.(isOpen)}
    </Accordion.Root>
  );
};
